import React, { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { createUseStyles, useTheme, Icon, Popover, Text } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { CopilotContext } from '../../../Context';
import {
    DEFAULT_INPUT_MODE,
    INPUT_MODE,
    INPUT_MODE_CONFIG,
    INPUT_MODE_SELECTOR_OPTIONS,
} from '../../../hooks/constants';

import styles from './styles';

const mergeFilesWithExisting = (existingFiles, nextFiles) =>
    existingFiles.length > 0 ? [...existingFiles, ...nextFiles] : nextFiles;

const useStyles = createUseStyles(styles, 'CopilotBarInputMode');

const MENU_ITEM_TYPES = {
    DIVIDER: 'divider',
    ICON: 'icon',
};

const CopilotBarInputMode = ({ onFilesChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const fileInputRef = useRef(null);
    const { t } = useWeb5Context();
    const classes = useStyles();
    const theme = useTheme();
    const { copilotBarProps, setCopilotBarProps } = useContext(CopilotContext);
    const existingFiles = useMemo(() => copilotBarProps?.files ?? [], [copilotBarProps?.files]);

    const handleVisibleChange = useCallback((visible) => {
        setIsOpen(visible);
    }, []);

    const handleFileSelection = useCallback(
        (event) => {
            const nextFiles = Array.from(event.target.files ?? []);

            if (onFilesChange) {
                if (nextFiles.length > 0) {
                    onFilesChange(mergeFilesWithExisting(existingFiles, nextFiles));
                } else {
                    onFilesChange([]);
                }
            } else if (nextFiles.length > 0) {
                setCopilotBarProps({
                    files: nextFiles,
                    inputMode: INPUT_MODE.FILE,
                    inputPlaceholder: null,
                });
            } else {
                setCopilotBarProps({
                    files: [],
                    inputMode: DEFAULT_INPUT_MODE,
                    inputPlaceholder: null,
                });
            }

            event.target.value = '';
        },
        [existingFiles, onFilesChange, setCopilotBarProps],
    );

    const openFileDialog = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleMenuItemClick = useCallback(
        (mode) => {
            if (mode === INPUT_MODE.FILE) {
                handleVisibleChange(false);
                openFileDialog();
                return;
            }

            // Explicitly clear inputPlaceholder to ensure CopilotBar derives the correct
            // default placeholder for the new mode, removing any previous overrides.
            setCopilotBarProps({ inputMode: mode, inputPlaceholder: null });
            handleVisibleChange(false);
        },
        [handleVisibleChange, openFileDialog, setCopilotBarProps],
    );

    const menuItems = useMemo(() => {
        return INPUT_MODE_SELECTOR_OPTIONS.flatMap((mode) => {
            const modeConfig = INPUT_MODE_CONFIG[mode];

            if (!modeConfig?.titleKey || !modeConfig.icon) {
                return [];
            }

            const items = [
                {
                    type: MENU_ITEM_TYPES.ICON,
                    icon: modeConfig.icon,
                    mode,
                    title: t(modeConfig.titleKey),
                },
            ];

            if (modeConfig.hasDividerAfter) {
                items.push({ type: MENU_ITEM_TYPES.DIVIDER });
            }

            return items;
        });
    }, [t]);

    const popoverContent = useMemo(
        () => (
            <div className={classes.menuContent}>
                {menuItems.map((item, index) => {
                    switch (item.type) {
                        case MENU_ITEM_TYPES.DIVIDER:
                            return <div key={`divider-${index}`} className={classes.divider} />;
                        case MENU_ITEM_TYPES.ICON:
                            return (
                                <div
                                    key={item.mode}
                                    className={classes.menuItem}
                                    onClick={() => handleMenuItemClick(item.mode)}
                                >
                                    <Icon name={item.icon} color={theme.colors.grey[500]} />
                                    <Text color={theme.colors.utility.textPrimary}>
                                        {item.title}
                                    </Text>
                                </div>
                            );
                    }
                })}
            </div>
        ),
        [
            classes.divider,
            classes.menuContent,
            classes.menuItem,
            handleMenuItemClick,
            menuItems,
            theme.colors.grey,
            theme.colors.utility.textPrimary,
        ],
    );

    const triggerElement = useMemo(
        () => (
            <span className={classNames(classes.root, { [classes.open]: isOpen })}>
                <Icon name="addPlus" color="currentColor" />
            </span>
        ),
        [classes.open, classes.root, isOpen],
    );

    const popoverProps = useMemo(
        () => ({
            className: classes.popover,
            content: popoverContent,
            onVisibleChange: handleVisibleChange,
            placement: 'topRight',
            visible: isOpen,
        }),
        [classes.popover, isOpen, handleVisibleChange, popoverContent],
    );

    return (
        <>
            <input hidden multiple ref={fileInputRef} type="file" onChange={handleFileSelection} />
            <Popover {...popoverProps}>{triggerElement}</Popover>
        </>
    );
};

CopilotBarInputMode.propTypes = {
    onFilesChange: PropTypes.func,
};

const MemoizedCopilotBarInputMode = memo(CopilotBarInputMode);
MemoizedCopilotBarInputMode.displayName = 'CopilotBarInputMode';

export default MemoizedCopilotBarInputMode;
